import { ReactNode, useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/lib/hooks'
import { checkAuthStatus } from '@/features/auth/authSlice'

interface ProtectedRouteProps {
  children: ReactNode
  redirectTo?: string
}

export default function ProtectedRoute({ 
  children, 
  redirectTo = '/login' 
}: ProtectedRouteProps) {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const { isAuthenticated, isLoading, user } = useAppSelector((state) => state.auth)

  // Check auth status on mount
  useEffect(() => {
    if (!isAuthenticated && !user) {
      dispatch(checkAuthStatus())
    }
  }, [dispatch, isAuthenticated, user])

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center shadow-lg">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          </div>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Checking authentication...
          </p>
        </div>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />
  }

  // Render protected content
  return <>{children}</>
} 